import { useState, useEffect } from "react";
import { auth } from "../firebase";

const X_API_KEY = process.env.REACT_APP_X_API_KEY || "0f247291-b4bb-4895-91ac-ba26af7e55a2";
const API_BASE = process.env.REACT_APP_APP_ENV === "local"
  ? "http://127.0.0.1:5001/twodata-dev/europe-west3/tenantapi"
  : "https://europe-west3-twodata-dev.cloudfunctions.net/tenantapi";

export function useStoryTemplate(templateId: string | null | undefined, companyId: string | null | undefined) {
  const [storyTemplate, setStoryTemplate] = useState<string>("");
  const [storyTemplateId, setStoryTemplateId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch story template
  useEffect(() => {
    const fetchStoryTemplate = async () => {
      if (!templateId || !companyId) return;
      setLoading(true);
      setError(null);
      try {
        const user = auth.currentUser;
        if (!user) {
          setError("User not authenticated");
          return;
        }
        const idToken = await user.getIdToken();
        const response = await fetch(`${API_BASE}/flexFormStoryTemplates/${templateId}?companyId=${companyId}`, {
          method: 'GET',
          headers: {
            "x-api-key": X_API_KEY,
            'Authorization': `Bearer ${idToken}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setStoryTemplate(data.handlebarsTemplate || "");
          setStoryTemplateId(templateId);
        } else if (response.status === 404) {
          setStoryTemplate("");
          setStoryTemplateId(null);
        } else {
          setError(`Error fetching story template: ${response.status}`);
        }
      } catch (err: any) {
        setError(err.message || "Error fetching story template");
      } finally {
        setLoading(false);
      }
    };
    fetchStoryTemplate();
  }, [templateId, companyId]);

  // Save story template
  const saveStoryTemplate = async (newTemplate: string, templateIdOverride?: string | null) => {
    const idToUse = templateIdOverride || templateId;
    if (!idToUse) throw new Error("Template ID is required to save story template");

    // If template is empty and no existing record was loaded, skip API call
    if (!newTemplate.trim() && !storyTemplateId) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");
      const idToken = await user.getIdToken();
      const body: any = {
        templateId: idToUse,
        companyId,
        active: !!newTemplate.trim(),
      };
      if (newTemplate.trim()) {
        body.handlebarsTemplate = newTemplate;
      }
      const response = await fetch(`${API_BASE}/flexFormStoryTemplates/${idToUse}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "x-api-key": X_API_KEY,
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      setStoryTemplate(newTemplate);
      setStoryTemplateId(idToUse);
    } catch (err: any) {
      setError(err.message || "Error saving story template");
      throw err; // Re-throw to let caller handle
    } finally {
      setLoading(false);
    }
  };

  return {
    storyTemplate,
    setStoryTemplate,
    storyTemplateId,
    loading,
    error,
    saveStoryTemplate,
  };
}
